import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { EnvironmentService } from '@core/feature';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { TodayTomorrowDatePipe } from '@sdvor/shared/ui';
import { Pos, SetShopIdEventType } from '@shop/const';
import { ShopWorkingTimeService } from '@shop/feature';
import { CxDatePipe } from '@spartacus/core';
import { ModalService } from '@spartacus/storefront';
import { GeocodeResult } from '@yandex-maps/const';
import { GeocodeService } from '@yandex-maps/feature';
import { BehaviorSubject, Observable, Subject, Subscription, combineLatest, concat, fromEvent, iif, of } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';

@Component({
  selector: 'sd-shop-list-desktop',
  templateUrl: './shop-list-desktop.component.html',
  styleUrls: ['./shop-list-desktop.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [TodayTomorrowDatePipe, CxDatePipe],
  standalone: false,
})
export class ShopListDesktopComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @Input() hasTitle: boolean;
  @Input() shopList: Pos[];
  @Input() getPlaceMarkTemplateFn: (shop: Pos, workingTime: string) => ymaps.IPlacemarkTemplate;
  @Input() suggestionSelectLabel = 'Введите Ваш адрес';
  @Input() closeModalOnConfirm = true;
  @Input() error = false;
  @Input() errorMessage = '';
  @Input() sortShops = true;
  @Input() isPinMyShop: boolean;
  @Input() myShopName = '';

  @Output() setShopId = new EventEmitter<string>();
  @Output() setShopIdEventType = new EventEmitter<SetShopIdEventType>();

  @ViewChild('errorTooltip', { static: false })
  errorTooltip: NgbTooltip | undefined;

  mapInstance: ymaps.Map;
  shopsSorted$: Observable<Pos[]>;
  activeId$: BehaviorSubject<string>;
  activeShop$: Observable<Pos>;
  form = new FormGroup({ address: new FormControl() });
  cityName: string;

  addressGeoObject$ = new Subject<GeocodeResult | undefined>();

  @ViewChild('shopListRef') shopListRef: ElementRef;

  @ContentChild('shopTemplateRef', { static: false }) shopTemplateRef: TemplateRef<any>;

  protected subscription = new Subscription();

  private shopsSubject$: BehaviorSubject<Pos[]> = new BehaviorSubject([]);

  constructor(
    protected modalService: ModalService,
    protected datePipe: TodayTomorrowDatePipe,
    private shopWorkingTimeService: ShopWorkingTimeService,
    protected cdRef: ChangeDetectorRef,
    private geocodeService: GeocodeService,
    public environmentService: EnvironmentService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.shopList?.currentValue) {
      this.shopsSubject$.next(this.shopList);
    }

    if (changes.error && this.errorMessage) {
      if (changes.error.currentValue) {
        this.errorTooltip?.open();
      } else {
        this.errorTooltip?.close();
      }
    }
  }

  ngAfterViewInit(): void {
    if (this.error) {
      this.errorTooltip.open();
    }
  }

  ngOnInit(): void {
    this.activeId$ = new BehaviorSubject(undefined);

    this.shopsSorted$ = iif(() => this.sortShops, this.getSortedShops(), this.shopsSubject$);

    this.activeShop$ = combineLatest([this.shopsSubject$, this.activeId$]).pipe(
      map(([shops, activeId]) => shops?.find((shop) => shop.name === activeId)),
    );

    this.subscription.add(
      this.addressGeoObject$.pipe(filter<GeocodeResult>(Boolean)).subscribe((address) => {
        const coords = address.geometry.getCoordinates();

        this.mapInstance.setCenter(coords);
        this.mapInstance.setZoom(13);

        this.mapInstance.geoObjects.each((geoObject) => {
          if (geoObject.options.get('id', {}) === 'userPlacemark') {
            this.mapInstance.geoObjects.remove(geoObject);
          }
        }, {});

        this.mapInstance.geoObjects.add(
          new ymaps.Placemark(
            coords,
            { iconCaption: address.getAddressLine().split(',').slice(2).join(',') },
            { id: 'userPlacemark', preset: 'islands#orangeCircleDotIcon' },
          ),
        );
      }),
    );

    if (this.environmentService.isPlatformBrowser) {
      // я создаю слушатель кликов, чтобы в этих кликах искать клик по кнопке "выбрать" внутри баллуна\кластера
      // Другого относительно удобного способа эмитить клик по кнопке внутри карты в ангуларе я быстро придумать не смог.
      // Если есть предложения лучше, жду подсказок.
      this.subscription.add(
        fromEvent(document, 'click')
          .pipe(tap((e: Event) => this.clickListener(e)))
          .subscribe(),
      );
    }
  }

  onChangeAddress(geocodeResult?: GeocodeResult): void {
    this.addressGeoObject$.next(geocodeResult);
  }

  onMapCreated(ymap: ymaps.Map): void {
    this.mapInstance = ymap;
  }

  clickListener(e: Event): void {
    if ((e.target as HTMLButtonElement).classList.contains('btn-pickup')) {
      this.confirmShop((e.target as HTMLButtonElement).dataset.id, SetShopIdEventType.Map);
    }
  }

  transformDate(date: Date | string): string {
    return this.datePipe.transform(date, 'dd MMMM');
  }

  confirmShop(id: string, eventType: SetShopIdEventType): void {
    this.setShopId.emit(id);
    this.setShopIdEventType.emit(eventType);
    this.setShop(id);
    if (this.closeModalOnConfirm) {
      this.modalService.closeActiveModal();
    }
  }

  setShop(id: string): void {
    this.activeId$.next(id);
    this.cdRef.markForCheck();
    this.scrollToActiveShop();
  }

  scrollToActiveShop(): void {
    const shop: HTMLDivElement = (this.shopListRef.nativeElement as HTMLDivElement).querySelector(
      `#id${this.activeId$.value}`,
    );
    if (shop) {
      this.shopListRef.nativeElement.scrollTop = shop.offsetTop - 10;
    }
  }

  clickToListShop(id: string, zoom: number = 14): void {
    this.activeId$.next(id);
    this.mapInstance.setZoom(zoom);
    this.selectShopMap(id);
    this.confirmShop(id, SetShopIdEventType.List);
  }

  resetActive(): void {
    this.activeId$.next(undefined);
  }

  selectShopMap(id: string, open: boolean = true): void {
    const openBaloon = (placemark: ymaps.Placemark): void => {
      if (id === placemark.properties?.get<string>('id', '')) {
        this.mapInstance.panTo(placemark.geometry.getCoordinates()).then(() => {
          if (open) {
            placemark.balloon.open();
          }
        });
      }
    };

    this.mapInstance.geoObjects.each((geoObject: ymaps.Placemark) => {
      switch (typeof geoObject.getGeoObjects) {
        case 'function':
          geoObject.getGeoObjects().forEach((mark: ymaps.Placemark) => {
            openBaloon(mark);
          });
          break;
        default:
          openBaloon(geoObject);
      }
    }, {});
  }

  getShopPlacemarkProperties = (shop: Pos): ymaps.IPlacemarkTemplate => {
    const workingTime = this.shopWorkingTimeService.getWorkingTimeInfo(shop).text;

    return this.getPlaceMarkTemplateFn
      ? this.getPlaceMarkTemplateFn(shop, workingTime)
      : this.getDefaultPlacemarkTemplate(shop, workingTime);
  };

  getDefaultPlacemarkTemplate = (shop: Pos, workingTime: string): ymaps.IPlacemarkTemplate => {
    return {
      balloonContentHeader: `<span class="balloon-content-header" style="font-size: 12px; font-weight: normal">${shop.addressFormatted}</span>`,
      balloonContentBody: `<div class="shop-worktime"
                                style="color: #666666; display: flex;gap: 12px; align-items: center;">
                              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <circle cx="7" cy="7" r="6.5"  fill="none" stroke="currentColor"/>
                                <path d="M7 3.5V7.56522L9 10" stroke="currentColor"/>
                              </svg>
                              <span>${workingTime}</span>
                            </div>`,
      balloonContentFooter: `<p style="margin-bottom: 15px !important">${
        shop.pickupDate ? this.transformDate(shop.pickupDate) : 'Дату самовывоза уточнит оператор'
      }</p><div><button data-id="${shop.name}"
      style="display:block;margin-bottom:1px; font-size: 12px; border-radius: 3px; width: 135px;
font-weight: normal; height: 25px" class="btn btn-pickup btn-primary btm-sm">Выбрать</button></div>`,
      clusterCaption: shop.addressFormatted,
      id: shop.name,
    };
  };

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  private getSortedShops(): Observable<Pos[]> {
    return combineLatest([this.shopsSubject$, concat(of(undefined), this.addressGeoObject$)]).pipe(
      map(([shops, addressGeoObject]) => {
        return addressGeoObject
          ? shops.map((shop: Pos) => {
              const shopCoords: ymaps.Coordinate = [shop.geoPoint.latitude, shop.geoPoint.longitude],
                addressCoords = addressGeoObject.geometry.getCoordinates();

              return {
                ...shop,
                distanceKm: this.geocodeService.getDistanceInKm(shopCoords, addressCoords),
              };
            })
          : shops;
      }),
      map((shops) => {
        if (!shops.length) {
          return shops;
        }

        const firstShop: Pos = shops[0];
        const hasDistanceKm = !!firstShop.distanceKm;

        const compareFn = hasDistanceKm
          ? (a: Pos, b: Pos): number => (a.distanceKm || 0) - (b.distanceKm || 0)
          : (a: Pos, b: Pos): number => b.available - a.available;

        if (this.isPinMyShop && !!this.myShopName) {
          return [firstShop, ...shops.slice(1).sort(compareFn)];
        } else {
          return shops.slice().sort(compareFn);
        }
      }),
    );
  }
}
